import { useCallback } from 'react';
import { Account } from 'shared/types/account';
import { Cart, Order } from 'shared/types/cart';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { ExtendedProductDetailProduct, ExtendedProductListingProduct } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { PaymentData, PAYMENT_METHODS } from 'components/commercetools-ui/organisms/checkout/provider/payment/types';
import {
  SEARCH,
  ADD_PAYMENT_INFO,
  ADD_SHIPPING_INFO,
  ADD_TO_CART,
  BEGIN_CHECKOUT,
  CART_UPDATED,
  LOGIN,
  LOGOUT,
  PURCHASE,
  REMOVE_FROM_CART,
  SELECT_ITEM,
  SIGN_UP,
  VIEW_CART,
  VIEW_ITEM,
  VIEW_ITEM_LIST,
  VIEW_ORDER_DETAILS,
  VIEW_ORDER_LIST,
  ACCOUNT_CREATED,
  ACCOUNT_VERIFIED,
  ACCOUNT_DETAILS_UPDATED,
  ADD_ADDRESS_INFO,
  COUPON_APPLIED,
  COUPON_FAILED,
  COUPON_REMOVED,
  INVOICE_DOWNLOADED,
  PASSWORD_CHANGED,
  ERROR_404,
  START_WIZARD,
  WIZARD_STEP_VIEWED,
  WIZARD_STEP_COMPLETED,
} from 'helpers/constants/events';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { addGlobalContext } from 'helpers/utils/enrichEvent';
import { DEFAULT_CURRENCY } from '../../../helpers/constants/ecommerce';
import { useGlobalContext } from '../useGlobalContext';

type GTM_LineItem = {
  item_id: string;
  item_name: string;
  item_variant?: string;
  item_sku?: string;
  item_variant_type?: string;
  index?: number;
  purchasable_status: boolean;
  starting_price?: number;
  price: number;
  quantity?: number;
  discount?: number;
  currency?: string;
};

type GTMAccountData = { ecomm_customer_id: string; marketing_optin: boolean };

const normalizeLineItem = (lineItem: ExtendedLineItem): GTM_LineItem => {
  return {
    item_id: lineItem?.productId ?? '',
    item_name: lineItem?.name ?? '',
    item_variant: lineItem?.variants?.length === 1 ? (lineItem.variants[0].sku ?? '') : '',
    item_variant_type: (lineItem?.variants?.length ?? 0) > 1 ? 'Variant Group' : 'Single Product',
    item_sku: (lineItem?.variants?.length ?? 0) > 1 ? (lineItem?.variants?.[0].sku ?? '') : lineItem?.variant?.sku,
    purchasable_status: lineItem?.price !== null, // TODO: Check if product is purchasable backend side
    starting_price: CurrencyHelpers.centAmountToNumber(lineItem?.price?.centAmount ?? 0),
    price: CurrencyHelpers.centAmountToNumber(lineItem?.price?.centAmount ?? 0),
    quantity: lineItem.count ?? 0,
    discount: CurrencyHelpers.centAmountToNumber(lineItem?.discountedPrice?.centAmount ?? 0),
    currency: lineItem.price?.currencyCode ?? DEFAULT_CURRENCY,
  };
};

const normalizeViewCartItems = (cart: Cart): GTM_LineItem[] => {
  return (
    cart.lineItems?.map((lineItem, index) => {
      return {
        index,
        ...normalizeLineItem(lineItem),
      };
    }) ?? []
  );
};

const normalizeProduct = (product: ExtendedProductListingProduct): GTM_LineItem => {
  const price = product?.startingAtPrice?.salePrice ? Number(product?.startingAtPrice?.salePrice) : 0;
  return {
    item_id: product.productId ?? '',
    item_name: product.name ?? '',
    item_sku: product.variants.length === 1 ? (product.variants[0].sku ?? '') : '',
    item_variant: product.variants.length === 1 ? (product.variants[0].sku ?? '') : '',
    item_variant_type: (product.variants.length ?? 0) > 1 ? 'Variant Group' : 'Single Product',
    purchasable_status: product.variants[0].price !== null, // TODO: Check if product is purchasable backend side
    starting_price: CurrencyHelpers.centAmountToNumber(product.startingAtPrice?.basePriceNumeric ?? 0),
    price: CurrencyHelpers.centAmountToNumber(price),
    quantity: 1,
    discount: CurrencyHelpers.centAmountToNumber(product?.startingAtPrice?.discountPercentage ?? 0),
  };
};

const normalizePaymentType = (payment: PaymentData) => {
  if (payment.type === PAYMENT_METHODS.SCHEME) {
    return 'Credit Card';
  }
  if (payment.type === PAYMENT_METHODS.PAYPAL) {
    return 'PayPal';
  }
  if (payment.type === PAYMENT_METHODS.GOOGLEPAY) {
    return 'Google Pay';
  }
  if (payment.type === PAYMENT_METHODS.APPLEPAY) {
    return 'Apple Pay';
  }
  return 'Unknown';
};

const normalizeAccountToGTM = (account: Account): GTMAccountData => {
  return {
    ecomm_customer_id: account.accountId ?? '',
    marketing_optin: account.marketingConsent ?? false,
  };
};

const getSubtotalPrice = (order: Order | Cart) => {
  let subtotalPrice = order.sum?.centAmount ?? 0;
  // Since .sum includes the sum of lineItems*quantity + possible shipping value, we remove it to get the subtotal.
  if (order.shippingInfo?.price?.centAmount && subtotalPrice) subtotalPrice -= order.shippingInfo.price.centAmount;
  return subtotalPrice;
};

const useGTM = () => {
  const globalContext = useGlobalContext();

  const trackEvent = (event: string, eventParams: Record<string, any>) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear previous event's data

    const enrichedParams = addGlobalContext(eventParams, globalContext);

    window.dataLayer.push({ event, ecommerce: enrichedParams });
  };

  const viewItemListEvent = (
    products: ExtendedProductListingProduct[],
    searchParam?: string,
    itemListName?: string,
    wizardConfiguration?: string,
  ) => {
    trackEvent(VIEW_ITEM_LIST, {
      currency: DEFAULT_CURRENCY, // TODO: Get currency from cart
      items: products.map((product) => normalizeProduct(product)),
      ...(searchParam && { searchParam }),
      ...(itemListName && { item_list_name: itemListName }),
      ...(wizardConfiguration && { wizard_configuration: wizardConfiguration }),
    });
  };

  const startWizardEvent = () => {
    trackEvent(START_WIZARD, {});
  };

  const wizardStepViewedEvent = (stepName: string, stepNumber: number) => {
    trackEvent(WIZARD_STEP_VIEWED, { step_name: stepName, step_number: stepNumber });
  };

  const wizardStepCompletedEvent = (stepName: string, stepNumber: number, userSelection: string) => {
    trackEvent(WIZARD_STEP_COMPLETED, {
      step_name: stepName,
      step_number: stepNumber,
      user_selection: userSelection,
    });
  };

  const selectItemEvent = (product: ExtendedProductListingProduct) => {
    trackEvent(SELECT_ITEM, {
      items: [normalizeProduct(product)],
    });
  };

  const searchEvent = useCallback(
    (searchTerm: string, searchType = 'global') => {
      const params = {
        search_term: searchTerm,
        search_type: searchType,
      };

      trackEvent(SEARCH, params);
    },
    [globalContext],
  );

  const addToCartEvent = useCallback(
    (cart: Cart) => {
      const params = {
        items: normalizeViewCartItems(cart),
      };
      trackEvent(ADD_TO_CART, params);
    },
    [globalContext, normalizeViewCartItems],
  );

  const viewItemEvent = (
    product: ExtendedProductDetailProduct & { trafficSource?: string; activeVariant?: ExtendedVariant },
  ) => {
    const activeVariant = product.activeVariant || product.variants[0];

    trackEvent(VIEW_ITEM, {
      currency: activeVariant?.price?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(activeVariant?.price?.centAmount ?? 0),
      items: [normalizeLineItem({ ...product, variant: activeVariant })],
      traffic_source: product.trafficSource ?? 'direct',
    });
  };

  const viewCartEvent = (cart: Cart) => {
    trackEvent(VIEW_CART, {
      currency: cart.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(cart)),
      items: normalizeViewCartItems(cart),
    });
  };

  const removeFromCartEvent = (lineItem: ExtendedLineItem) => {
    trackEvent(REMOVE_FROM_CART, {
      currency: lineItem.price?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(lineItem.price?.centAmount ?? 0),
      items: [normalizeLineItem(lineItem)],
    });
  };

  const cartUpdatedEvent = () => {
    trackEvent(CART_UPDATED, {});
  };

  const beginCheckoutEvent = (cart: Cart) => {
    trackEvent(BEGIN_CHECKOUT, {
      currency: cart.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(cart)),
      coupon: cart.discountCodes?.[0]?.code ?? '',
      items: normalizeViewCartItems(cart),
    });
  };

  const addAddressInfoEvent = () => {
    trackEvent(ADD_ADDRESS_INFO, {});
  };

  const addShippingInfoEvent = (cart: Cart) => {
    trackEvent(ADD_SHIPPING_INFO, {
      currency: cart.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(cart)),
      shipping_tier: cart.shippingInfo?.shippingMethodName ?? cart.shippingInfo?.name ?? '',
      items: normalizeViewCartItems(cart),
    });
  };

  const addPaymentInfoEvent = (cart: Cart, paymentData: PaymentData) => {
    trackEvent(ADD_PAYMENT_INFO, {
      currency: cart.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(cart)),
      coupon: cart.discountCodes?.[0]?.code ?? '',
      payment_type: normalizePaymentType(paymentData),
      items: normalizeViewCartItems(cart),
    });
  };

  const purchaseEvent = (order: Order) => {
    trackEvent(PURCHASE, {
      currency: order.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(order)),
      coupon: order.discountCodes?.[0]?.code ?? '',
      items: normalizeViewCartItems(order),
      transaction_id: order?.orderId ?? '',
      shipping: CurrencyHelpers.centAmountToNumber(order?.shippingInfo?.price?.centAmount ?? 0),
      tax: CurrencyHelpers.centAmountToNumber(order?.taxed?.taxAmount?.centAmount ?? 0),
    });
  };

  const loginEvent = (method: string, account: Account) => {
    trackEvent(LOGIN, {
      method,
      ...normalizeAccountToGTM(account),
    });
  };

  const logoutEvent = (account: Account) => {
    trackEvent(LOGOUT, {
      ...normalizeAccountToGTM(account),
    });
  };

  const signUpEvent = (method: string) => {
    trackEvent(SIGN_UP, {
      method,
    });
  };

  const viewOrderListEvent = (orders: Order[]) => {
    trackEvent(VIEW_ORDER_LIST, {
      orders: orders.map((order) => ({
        order_id: order?.orderId ?? '',
      })),
    });
  };

  const viewOrderEvent = (order: Order) => {
    trackEvent(VIEW_ORDER_DETAILS, {
      order_id: order?.orderId ?? '',
    });
  };

  const accountCreatedEvent = (account: Account) => {
    trackEvent(ACCOUNT_CREATED, normalizeAccountToGTM(account));
  };

  const accountVerifiedEvent = (account: Account) => {
    trackEvent(ACCOUNT_VERIFIED, normalizeAccountToGTM(account));
  };

  const accountDetailsUpdatedEvent = (account: Account) => {
    trackEvent(ACCOUNT_DETAILS_UPDATED, normalizeAccountToGTM(account));
  };

  const couponAppliedEvent = (couponCode: string) => {
    trackEvent(COUPON_APPLIED, {
      coupon_code: couponCode,
    });
  };

  const couponFailedEvent = (couponCode: string) => {
    trackEvent(COUPON_FAILED, {
      coupon_code: couponCode,
    });
  };

  const couponRemovedEvent = (couponCode: string) => {
    trackEvent(COUPON_REMOVED, {
      coupon_code: couponCode,
    });
  };

  const invoiceDownloadedEvent = (orderId?: string) => {
    trackEvent(INVOICE_DOWNLOADED, {
      ...(orderId && { order_id: orderId }),
    });
  };

  const passwordChangedEvent = () => {
    trackEvent(PASSWORD_CHANGED, {});
  };

  const error404Event = (actionName?: string, endpoint?: string) => {
    trackEvent(ERROR_404, {
      ...(actionName && { action_name: actionName }),
      ...(endpoint && { endpoint }),
      error_type: '404',
      error_message: 'Not Found',
    });
  };

  return {
    viewItemListEvent,
    startWizardEvent,
    wizardStepViewedEvent,
    wizardStepCompletedEvent,
    selectItemEvent,
    searchEvent,
    addToCartEvent,
    cartUpdatedEvent,
    viewItemEvent,
    viewCartEvent,
    removeFromCartEvent,
    beginCheckoutEvent,
    addAddressInfoEvent,
    addShippingInfoEvent,
    addPaymentInfoEvent,
    purchaseEvent,
    loginEvent,
    logoutEvent,
    signUpEvent,
    viewOrderListEvent,
    viewOrderEvent,
    accountCreatedEvent,
    accountVerifiedEvent,
    accountDetailsUpdatedEvent,
    couponAppliedEvent,
    couponFailedEvent,
    couponRemovedEvent,
    invoiceDownloadedEvent,
    passwordChangedEvent,
    error404Event,
  };
};

export default useGTM;
